import React, {useCallback, useState, useRef, Fragment, useMemo, useEffect} from 'react';
import styled from 'styled-components';
import {
	pagingOptions,
	relatedDocumentsTab,
	documentTypes,
	entityTypes,
	fileTypesUpload,
	documentTabLayoutTabs,
	storageKeys
} from '../../../util/uiconstants';
import {isNullOrUndefined, labels} from '../../../util/utils';
import Search from '../BaseComponentWrappers/MotifSearch';
import Pagination from '../Pagination/Pagination';
import EllipsesControl from '../EllipsesControl/EllipsesControl';
import RelatedDocumentsItem from '../CanvasForm/Common/RelatedDocumentsItem';
import DuplicateDocuments from '../DuplicateDocuments/DuplicateDocuments';
import FileUpload from '../FileUpload/FileUpload';
import ReactiveReviewModal from '../ReactiveReviewModal';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import {clearItem, saveStorage} from '../localStorage';

export default function RelatedDocumentsCommon({
	activeTab,
	relatedDocuments,
	isLoading,
	setIsLoading,
	setDoSearch,
	paging,
	setPaging,
	isTargetEng,
	userPreferences,
	documentId,
	engagementId,
	isSearchEnabled = true,
	showDeleteMenuOption = false,
	showAddDocument = false,
	isSummaryView,
	isFormQuickReference,
	entityUId,
	isFromDocumentUpload,
	removeDocumentFromBody,
	showOptions = true,
	noDocsMessage,
	showPriorPeriod = false,
	isAresView = false,
	requireValidations = false,
	parentName = documentTabLayoutTabs.evidenceName,
	getRelatedDocuments,
	showAddDocumentsOnly,
	payloadRef,
	enablePriorPeriodOptions = false,
	enableTemporaryOptions = false,
	enableExternalDocumentsOptions = false,
	documentType = documentTypes.EVIDENCE,
	uploadDocumentEntityType = entityTypes.Task,
	relatedTasks,
	filteredEntityType,
	groupInstructionDrawer = false /* Open the component from the group audit drawer.*/,
	showAddTemporary = false,
	showExternalDocuments = false,
	isGroupRiskAssessmentSection = false,
	showRelatedTasks = false,
	instructionId,
	signoffRequirements,
	hasRelateTask,
	uploadRef,
	isFromDrawer,
	canvasFormOpenType,
	showMenuOptions,
	showPaging = true,
	showNotes = true,
	bodyTypeId,
	showCrashDocumentWarningMessage = false,
	dragDropEnabled = true,
	isDisabled,
	isModalOpen
}) {
	const ref = useRef();

	const fileUploadRef = uploadRef || ref;
	const [currentPage, setCurrentPage] = useState(1);
	const [searchText, setSearchText] = useState('');
	const [pageSize, setPageSize] = useState(pagingOptions.defaultPageSize);
	const [totalPages, setTotalPages] = useState(1);
	const [resetKey, setResetKey] = useState(1);

	const styleClass = useMemo(() => {
		return activeTab === relatedDocumentsTab.EVIDENCE && !isLoading ? 'evidence' : 'prior-period';
	}, [activeTab, isLoading]);

	const onChangeSearchText = (event) => {
		setSearchText(event.target.value);
	};

	const onSearch = () => {
		setPaging({...paging, currentPage: 1});
		setDoSearch(true);
	};

	const onSearchClear = () => {
		setPaging({...paging, currentPage: 1});
		setCurrentPage(1);
		setSearchText('');
		setDoSearch(true);
	};

	const documents = useMemo(() => {
		if (Array.isArray(relatedDocuments?.data) && relatedDocuments.data.length > 0) {
			const tabbedDocuments = relatedDocuments.data.filter((doc) => {
				if (activeTab === relatedDocumentsTab.PRIOR_PERIOD) {
					return doc.documentTypeId === documentTypes.PRIORYEAREVIDENCE;
				}

				if (activeTab === relatedDocumentsTab.EXTERNAL_DOCUMENTS) {
					return doc.documentTypeId === documentTypes.CLIENTFILES;
				}

				return true;
			});

			setTotalPages(relatedDocuments?.pages || 1);

			return tabbedDocuments;
		} else {
			setTotalPages(1);
			return [];
		}
	}, [relatedDocuments, activeTab]);

	const isEvidence = useMemo(() => {
		return activeTab === relatedDocumentsTab.EVIDENCE;
	}, [activeTab]);

	const showNotesCondition = useMemo(() => {
		const hasTabs = [relatedDocumentsTab.EVIDENCE, relatedDocumentsTab.TEMPORARY];

		if ((hasTabs.includes(activeTab) || activeTab !== relatedDocumentsTab.EXTERNAL_DOCUMENTS) && showNotes) {
			return true;
		}

		return false;
	}, [activeTab, showNotes]);

	const showRelatedTasksCondition = useMemo(() => {
		const excludingTabs = [
			relatedDocumentsTab.TEMPORARY,
			relatedDocumentsTab.EXTERNAL_DOCUMENTS,
			relatedDocumentsTab.PRIOR_PERIOD
		];

		if (!excludingTabs.includes(activeTab) && showRelatedTasks) {
			return true;
		}

		return false;
	}, [activeTab, showRelatedTasks]);

	const showNoResultMessage = useMemo(() => {
		if (isEvidence && dragDropEnabled && !groupInstructionDrawer) {
			return false;
		}

		const orCondition1 = !isAresView && activeTab === relatedDocumentsTab.EVIDENCE;
		const orCondition2 = activeTab === relatedDocumentsTab.PRIOR_PERIOD;

		return (
			showPriorPeriod || showAddTemporary || showExternalDocuments || orCondition1 || orCondition2 || !dragDropEnabled
		);
	}, [
		isAresView,
		activeTab,
		isEvidence,
		dragDropEnabled,
		groupInstructionDrawer,
		showPriorPeriod,
		showAddTemporary,
		showExternalDocuments
	]);

	useEffect(() => {
		saveStorage(`${storageKeys.bodyPagination}_${entityUId}`, {pageSize, currentPage});
	}, [pageSize, currentPage, entityUId]);

	useEffect(() => {
		return () => {
			clearItem(`${storageKeys.bodyPagination}_${entityUId}`);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (isLoading) {
		return <LoadingIndicator show fullscreen={false} view="inlineView" />;
	}

	return (
		<RelatedDocumentsCommonWrapper
			groupInstructionDrawer={groupInstructionDrawer}
			className={`relatedDocumentsWrapperContainer ${documents.length === 0 && 'no-documents'} ${!showNoResultMessage &&
				'relatedDocumentsWrapperContainerHide'}`}
		>
			{!isSummaryView && (
				<section className="relatedDocuments__tools">
					{isSearchEnabled && (
						<section className="relatedDocuments__search">
							<Search
								id="searchbox-documents"
								styleName="relatedDocSearch"
								value={searchText}
								onChange={onChangeSearchText}
								placeholder={labels.searchDocuments}
								onSearchButtonClick={onSearch}
								onEnter={onSearch}
								onClear={onSearchClear}
							/>
						</section>
					)}
					{(showAddDocument || showAddTemporary) && !isLoading && (
						<section className="addDocumentWrapper">
							<FileUpload
								ref={fileUploadRef}
								parentName={parentName}
								allowedOutputFileExtensions={fileTypesUpload.options}
								entityType={uploadDocumentEntityType}
								documentType={documentType}
								dragDropEnabled={false}
								uploadBrowseEnabled={false}
								overrideClassName="upload-browse"
								payloadRef={payloadRef}
								bodyId={entityUId}
								relatedTasks={relatedTasks}
								signoffRequirements={signoffRequirements}
								bodyTypeId={bodyTypeId}
								showCrashDocumentWarningMessage={showCrashDocumentWarningMessage}
								isDisabled={isDisabled}
							/>
						</section>
					)}
				</section>
			)}
			{documents.length > 0 ? (
				<section className="relatedDocuments__container">
					<section
						className={`relatedDocuments__table ${
							isAresView && documents.length === 0 ? 'noDataRelatedDocuments' : ''
						} ${styleClass}`}
					>
						{!isLoading && (
							<section className="relatedDocuments__header">
								<section className="relatedDocumentsHeaderName">
									<EllipsesControl id="header1" content={labels.name} tooltip={labels.name} />
								</section>
								{showNotesCondition && (
									<section className="relatedDocumentsHeaderNotes">
										<EllipsesControl id="header2" content={labels.comments} tooltip={labels.comments} />
									</section>
								)}
								{activeTab !== relatedDocumentsTab.TEMPORARY && activeTab !== relatedDocumentsTab.EXTERNAL_DOCUMENTS && (
									<section className="relatedDocumentsHeaderSignoff">
										<EllipsesControl id="header3" content={labels.signoffs} tooltip={labels.signoffs} />
									</section>
								)}
								{showRelatedTasksCondition && (
									<section className="relatedDocumentsHeaderRelatedTasks">
										<EllipsesControl
											id="header4"
											content={labels.relatedTasksTitle}
											tooltip={labels.relatedTasksTitle}
										/>
									</section>
								)}
							</section>
						)}
						{!showAddDocumentsOnly && (
							<section className="relatedDocuments__body customScrollbar" key={activeTab}>
								<ReactiveReviewModal entityUId={entityUId} />
								{documents.map((doc, index) => (
									<RelatedDocumentsItem
										key={`${doc.id}_${resetKey}`}
										setResetKey={setResetKey}
										canvasFormOpenType={canvasFormOpenType}
										userPreferences={userPreferences}
										document={doc}
										documentId={documentId}
										isTargetEng={isTargetEng}
										engagementId={engagementId}
										isFormQuickReference={isFormQuickReference}
										showDeleteMenuOption={showDeleteMenuOption}
										showMenuOptions={showMenuOptions}
										entityUId={entityUId}
										isFromDocumentUpload={isFromDocumentUpload}
										removeDocumentFromBody={removeDocumentFromBody}
										parentContainerClassName={'relatedDocuments__body'}
										showOptions={showOptions}
										isAresView={isAresView}
										activeTab={activeTab}
										requireValidations={requireValidations}
										parentName={parentName}
										isDragAndDropDoc={doc.isDragAndDrop}
										enablePriorPeriodOptions={enablePriorPeriodOptions}
										enableTemporaryOptions={enableTemporaryOptions}
										enableExternalDocumentsOptions={enableExternalDocumentsOptions}
										filteredEntityType={filteredEntityType}
										getRelatedDocuments={getRelatedDocuments}
										relatedTasks={doc?.tasks}
										isGroupRiskAssessmentSection={isGroupRiskAssessmentSection}
										groupInstructionDrawer={groupInstructionDrawer}
										instructionId={instructionId}
										showRelatedTasks={
											activeTab !== relatedDocumentsTab.TEMPORARY &&
											activeTab !== relatedDocumentsTab.EXTERNAL_DOCUMENTS &&
											activeTab !== relatedDocumentsTab.PRIOR_PERIOD &&
											showRelatedTasks
										}
										hasRelateTaskOption={hasRelateTask}
										showNotes={showNotes}
										isDisabled={isDisabled}
										isModalOpen={isModalOpen}
										itemClassName={`${index % 2 === 0 ? 'even' : 'odd'}`}
									/>
								))}
							</section>
						)}
					</section>
					{showPaging && (
						<section className="relatedDocuments__pagination">
							<Pagination
								optionsPerPage={pagingOptions.options}
								page={currentPage}
								pageSize={pageSize}
								pageCount={totalPages}
								onPageSizeChanged={(pageSize, searchString) => {
									setPageSize(pageSize);
									setCurrentPage(1);
									setSearchText(searchString);
									getRelatedDocuments &&
										getRelatedDocuments(searchString, 1, pageSize).finally(() => setIsLoading && setIsLoading(false));
								}}
								onPageChanged={(page) => {
									getRelatedDocuments &&
										getRelatedDocuments(searchText, page, pageSize).finally(() => setIsLoading && setIsLoading(false));
									setCurrentPage(page);
								}}
							/>
						</section>
					)}
				</section>
			) : (
				showNoResultMessage && (
					<section className={`relatedDocuments__table noDataRelatedDocuments prior-period`}>
						<section className="relatedDocuments__body customScrollbar">
							<section className="relatedDocuments__noDocs">
								{noDocsMessage !== '' && !isNullOrUndefined(noDocsMessage) ? noDocsMessage : labels.noRelatedDocuments}
							</section>
						</section>
					</section>
				)
			)}
			<DuplicateDocuments
				parentName={parentName}
				getRelatedDocuments={getRelatedDocuments}
				className={!isAresView && 'kronos-duplicate-document-modal'}
				relatedTasks={relatedTasks}
				documentType={documentType}
				isFromDrawer={isFromDrawer}
				showCrashDocumentWarningMessage={showCrashDocumentWarningMessage}
			/>
		</RelatedDocumentsCommonWrapper>
	);
}

const RelatedDocumentsCommonWrapper = styled.section`
	width: calc(100% - var(--px-80));
	border: var(--px-1) solid var(--neutrals-300);
	background-color: var(--neutrals-00white);
	margin-top: var(--px-20);
	&.relatedDocumentsWrapperContainer {
		height: var(--px-400);
		min-height: ${(props) => (!props.groupInstructionDrawer ? 'var(--px-400)' : 'var(--px-20)')};
		position: relative;
		&.no-documents {
			min-height: var(--px-200);
			height: auto;
			.relatedDocuments__tools {
				padding: var(--px-5) var(--px-10) !important;
			}
			&.relatedDocumentsWrapperContainerHide {
				display: none;
			}
		}
	}
	.RelateDocumentsTab {
		padding: var(--px-10) 0 0 0;
	}
	.relatedDocuments__tools {
		display: flex;
		padding: var(--px-5) var(--px-10);
		line-height: normal;
		.addDocumentWrapper {
			padding: 0 var(--px-10) 0 var(--px-25);
			width: 100%;
			.addEvidenceContainer {
				display: flex;
				max-width: 100%;
				align-items: center;
				button {
					padding: 0;
					margin-right: var(--px-5);
				}
				.motif-icon {
					margin-right: var(--px-5);
					svg {
						path {
							fill: var(--blue-600) !important;
						}
						width: var(--px-20) !important;
						height: var(--px-20) !important;
					}
				}
			}
			.motif-dropdown {
				.motif-dropdown-trigger {
					display: flex;
					align-items: center;
					height: 100%;
					.motif-icon {
						margin-right: var(--px-5);
						svg {
							path {
								fill: var(--blue-600) !important;
							}
							width: var(--px-20) !important;
							height: var(--px-20) !important;
						}
					}
				}
			}
		}
	}
	.relatedDocuments__table {
		display: flex;
		flex-direction: column;
		border: var(--px-1) solid var(--neutrals-200);
		.relatedDocuments__header {
			display: flex;
			background: var(--neutrals-25);
			color: var(--neutrals-900);
			border-bottom: var(--px-1) solid var(--neutrals-300);
			padding: var(--px-10) 0 var(--px-10) var(--px-20);
			font-size: var(--px-14);
			height: var(--px-48);
			align-items: center;
			.relatedDocumentsHeaderName {
				flex: 1 1 0;
				display: flex;
				width: calc(100% - var(--px-250));
				font-size: var(--px-14);
				font-weight: bold;
				padding-left: var(--px-5);
			}
			.relatedDocumentsHeaderNotes {
				width: var(--px-80);
				font-size: var(--px-14);
				font-weight: bold;
				display: flex;
				justify-content: center;
				.ellipses {
					width: auto;
				}
			}
			.relatedDocumentsHeaderSignoff {
				width: var(--px-210);
				padding-left: var(--px-20);
				font-size: var(--px-14);
				font-weight: bold;
				display: flex;
				justify-content: flex-start;
				.ellipses {
					width: auto;
				}
			}
			.relatedDocumentsHeaderRelatedTasks {
				width: var(--px-100);
				font-size: var(--px-14);
				font-weight: bold;
				display: flex;
				justify-content: center;
				.ellipses {
					width: auto;
				}
			}
			div {
				display: block !important;
			}
		}
		&.prior-period {
			min-height: var(--px-100);
			.relatedDocuments__header {
				.relatedDocumentsHeaderName {
					width: calc(100% - var(--px-180));
				}
				.relatedDocumentsHeaderNotes {
					display: none;
				}
			}
			.relatedDocuments__row {
				.relatedDocumentDocName {
					width: calc(100% - var(--px-250));
					.DocumentNameWrapper {
						&.AdvanceDocName {
							width: 100% !important;
							.ProgressBarContainer {
								min-width: var(--px-40);
								text-align: center;
								display: inline-flex;
								margin-right: var(--px-30);
								.motif-progress-bar-wrapper {
									.motif-progress-bar {
										svg {
											width: auto;
											height: auto;
											fill: transparent;
										}
									}
								}
							}
						}
					}
				}
			}
		}
		&.noDataRelatedDocuments {
			border: 0;
			justify-content: center;
			.relatedDocuments__noDocs {
				padding: 0;
			}
		}
	}
	.relatedDocuments__title {
		display: flex;
		align-items: center;

		h3 {
			display: inline-flex;
			margin: 0;
		}

		.relatedDocuments__moremenu {
			display: inline-flex;
			margin-left: auto;
			margin-right: var(--px-20);
			svg {
				fill: var(--neutrals-900);
			}
			.motif-dropdown {
				height: var(--px-24);
				.motif-dropdown-trigger {
					height: var(--px-24);
				}
				.motif-dropdown-menu {
					width: var(--px-250);
				}
			}
		}
	}
	.relatedDocuments__container {
		display: flex;
		flex-direction: column;

		.relatedDocuments__pagination {
			display: flex;
			align-items: center;
			justify-content: center;
			height: var(--px-60);
			padding-right: var(--px-20);
			margin-right: var(--px-5);

			.paging__show__text {
				position: relative;
				display: flex;
				justify-content: end;
				width: 100%;
				margin-right: var(--px-5);
				bottom: var(--px-1);
				font-size: var(--px-14);
				font-weight: 300;
				line-height: var(--px-22);
			}

			.motif-pagination {
				.motif-pagination-select-wrapper {
					.motif-dropdown {
						.motif-dropdown-trigger {
							.motif-icon-button {
								width: var(--px-50);
							}
						}
					}
				}
			}
		}
	}
	.relatedDocuments__body {
		display: flex;
		flex-direction: column;
		min-height: auto;
		max-height: var(--px-1330);
		overflow-y: auto;
		color: var(--neutrals-900);
		font-weight: 300;
		background: var(--neutrals-00white);
	}
	.relatedDocuments__row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0;
		position: relative;
		&.even {
			background-color: var(--neutrals-25);
		}
		&.odd {
			background-color: var(--neutrals-00white);
		}
		.relatedDocumentDocName {
			display: inline-flex;
			width: calc(100% - var(--px-250));
			.DocumentNameContainer {
				&.name-link {
					cursor: default;
					&:hover,
					&:focus {
						text-decoration: none !important;
					}
				}
				.DocumentSVG {
					svg {
						font-size: var(--px-12) !important;
					}
				}
				.DocumentNameDetails {
					.DocumentName {
						display: inline-block;
						width: calc(100% - var(--px-100));
						.DocumentNameText {
							width: 100%;
							.DocumentName {
								color: var(--neutrals-900);
								width: auto;
								display: inline-block;
							}
						}
						.last-modified,
						.currently-modified,
						.coedit-mode-complete {
							font-size: var(--px-12);
						}
						.coedit-mode-complete {
							display: flex;
							.coedit-mode-complete-label {
								margin-right: var(--px-3);
								max-width: 90%;
								display: inline-block;
							}
						}
					}
					.documentName-icons {
						display: flex;
						margin-left: auto;
						margin-top: 0;
						color: var(--neutrals-900);
						svg {
							color: var(--neutrals-900);
							fill: var(--neutrals-900);
							path {
								fill: var(--neutrals-900);
							}
						}
						.document-checked-out-icon {
							margin-right: var(--px-10);
						}
						.motif-tooltip-wrapper {
							align-items: unset;
						}
					}
					.input-container {
						.file-name-input {
							border: 0;
							color: var(--neutrals-900);
							background-color: transparent !important;
						}
					}
					.note-error {
						color: var(--neutrals-900);
						font-size: var(--px-12);
						font-weight: 300;
						line-height: var(--px-20);
						svg {
							color: var(--neutrals-900);
							fill: var(--neutrals-900);
							path {
								fill: var(--neutrals-900);
							}
						}
					}
				}
			}
			.cogmenu-btn {
				display: flex;
				svg {
					fill: var(--neutrals-700) !important;
					color: var(--neutrals-700) !important;
					width: var(--px-20);
					height: var(--px-20);
				}
				.show-pop-content {
					&.bottom {
						top: var(--px-30);
					}
					&.top {
						bottom: var(--px-45);
					}
				}
			}
		}
		.small {
			padding: 0;
			width: 100%;
		}
		section:not([class]) {
			color: var(--neutrals-700);
			font-size: var(--px-12);
			padding-left: var(--px-5);
			.modifiedName {
				margin-right: var(--px-10);
			}
		}
		.DocumentNameWrapper {
			padding: var(--px-10) var(--px-20) 0 var(--px-20);
			position: relative;
			.DocumentNameDetails {
				display: inline-flex;
				.DocumentNameText {
					.DocumentName {
						height: auto;
					}
				}
				/* .DocumentShared {
					margin-left: auto;
					margin-right: var(--px-10);
				} */
				.documentName-icons {
					color: var(--neutrals-900);
					svg {
						color: var(--neutrals-900);
						fill: var(--neutrals-900);
						path {
							fill: var(--neutrals-900);
						}
					}
				}
			}
			.ProgressBarContainer {
				position: absolute;
				right: var(--px-10);
				height: var(--px-40);
				background: var(--neutrals-00white);
				z-index: 99;
				width: 100%;
				height: 100% !important;
				.motif-progress-bar-wrapper {
					position: relative;
					top: 0;
					width: 60%;
					left: var(--px-10);
					margin: 0 auto;
					.motifProgressBarLabel {
						display: flex;
					}
				}
			}
		}
		&.disable {
			user-select: none;
			pointer-events: none;
			.DocumentNameWrapper {
				color: var(--neutrals-500);
			}
		}
		.body-quick-reference_row_review-notes {
			border-right: var(--px-1) solid var(--neutrals-300);
			height: var(--px-60);
			padding: var(--px-10) var(--px-10);
			width: var(--px-70);
			display: inline-flex;
			justify-content: center;
			align-items: center;
			.notesControl {
				padding: 0;
				.motif-button {
					span {
						color: var(--neutrals-900);
						svg {
							color: var(--neutrals-900);
						}
					}
					&.simple-button {
						height: var(--px-18);
						width: var(--px-18);
						border: var(--px-1) solid var(--neutrals-900);
						border-radius: var(--px-50);
						color: var(--neutrals-900);
						font-size: var(--px-12);
						span {
							padding: 0;
							width: 100%;
							height: 100%;
							display: flex;
							align-items: center;
							justify-content: center;
						}
						&:hover {
							background-color: var(--neutrals-900);
							span {
								color: var(--neutrals-00white);
								svg {
									fill: var(--neutrals-00white);
								}
							}
						}
						&.removeBorder {
							border: 0;
							&:hover {
								background-color: transparent;
								span {
									color: var(--neutrals-900);
									svg {
										fill: var(--neutrals-900);
									}
								}
							}
						}
					}
				}
			}
		}
	}
	.body-quick-reference__row__sign-offs {
		display: flex;
		align-items: center;
		padding: 0 var(--px-10);
		height: var(--px-38);
		width: var(--px-200);
		.evidenceSignOff {
			padding: 0;
			min-width: var(--px-200);
			/* .StyledSignOffItem {
				.signOff-preparer {
					.sign-off-icon {
						background-color: var(--green-400) !important;
						&:hover,
						&:focus {
							background-color: var(--green-400) !important;
						}
					}
					.signOff-reviewer noSignOffs {
						border: var(--px-1) solid var(--neutrals-900);
						background-color: transparent !important;
						&:hover,
						&:focus {
							background-color: transparent !important;
						}
					}
					&:hover,
					&:focus {
						background-color: var(--green-400);
					}
				}
				.signOff-reviewer {
					.sign-off-icon {
						background-color: var(--red-600) !important;
						&:hover,
						&:focus {
							background-color: var(--red-600) !important;
						}
					}
					.signOff-reviewer noSignOffs {
						border: var(--px-1) solid var(--neutrals-900);
						background-color: transparent !important;
						&:hover,
						&:focus {
							background-color: transparent !important;
						}
					}
					&:hover,
					&:focus {
						background-color: var(--red-600);
					}
				}
			} */
		}
	}
	.relatedDocumentsWrapper {
		display: flex;
	}
	.relatedDocuments__search {
		display: inline-flex;
		width: var(--px-300);
		vertical-align: middle;
		margin: var(--px-5) 0;
		.relatedDocSearch {
			width: 100%;
			display: flex;
		}
	}
	.relatedDocumentsPagination {
		margin-left: auto;
		display: flex;
		margin: var(--px-5) 0;
		.StyledPagination {
			display: flex;
		}
	}
	.relatedDocuments__noDocs {
		padding-top: var(--px-25);
		display: inline-block;
		width: 100%;
		text-align: center;
		font-size: var(--px-16);
		color: var(--neutrals-700);
		font-weight: normal;
	}
	.relatedDocuments__dropdownItem {
		padding: var(--px-5) 0;
	}
	.motif-tooltip-wrapper {
		margin-left: auto;
		margin-right: var(--px-10);
		align-items: center;
	}
`;
